<template>
  <div
    v-if="show"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-xs"
    @click="$emit('close')"
  >
    <div
      class="animate-pop max-h-[85vh] w-full max-w-md overflow-y-auto rounded-2xl border-2 border-primary bg-white p-6 shadow-2xl"
      @click.stop
    >
      <!-- 头部 -->
      <div class="mb-3 flex items-center justify-between border-b border-border pb-3">
        <h2 class="font-heading text-xl sm:text-2xl font-bold tracking-wider text-primary flex items-center gap-2">
          <span>🎉</span>
          <span>金榜题名 · 恭喜通关！</span>
        </h2>
        <button
          type="button"
          @click="$emit('close')"
          class="text-2xl text-muted hover:text-clay cursor-pointer p-1"
          aria-label="关闭"
        >
          ×
        </button>
      </div>

      <!-- 仕途升迁庆典横幅 (当 isPromoted: true 时华丽触发) -->
      <div
        v-if="checkpointResult?.isPromoted"
        class="mb-4 rounded-xl bg-gradient-to-r from-amber-500 via-red-500 to-amber-600 p-3 text-white shadow-md animate-bounce-subtle"
      >
        <div class="flex items-center gap-2.5">
          <div class="text-2xl">📜</div>
          <div>
            <div class="text-xs font-bold tracking-widest uppercase opacity-90">仕途升迁 · 敕封金牒</div>
            <div class="text-sm font-extrabold mt-0.5">
              奉天承运，特擢升为【{{ checkpointResult?.rankTitle }}】！
            </div>
          </div>
        </div>
      </div>

      <!-- 战绩与官衔结算卡片 -->
      <div v-if="checkpointResult" class="mb-4 rounded-xl border border-amber-900/15 bg-amber-50/60 p-3 flex items-center justify-between">
        <div class="flex items-center gap-2.5">
          <div class="h-10 w-10 rounded-lg bg-amber-800 text-amber-50 font-bold flex flex-col items-center justify-center text-xs shadow-xs font-serif">
            <span>{{ checkpointResult.rankTitle?.slice(0, 2) }}</span>
          </div>
          <div>
            <div class="text-xs font-semibold text-stone-900 flex items-center gap-1.5">
              <span>仕途：{{ checkpointResult.rankTitle }}</span>
              <span class="rounded bg-amber-200/80 px-1 py-0.2 text-[10px] text-amber-900 font-mono">阶位 {{ checkpointResult.rankLevel }}</span>
            </div>
            <div class="text-[11px] text-amber-900/70 mt-0.5 font-sans">
              累积最高通关：第 {{ checkpointResult.highestLevel }} 关
            </div>
          </div>
        </div>

        <div class="text-right">
          <div class="text-xs font-bold text-amber-700 font-sans">
            +{{ checkpointResult.starsGained }} ★
          </div>
          <div class="text-[10px] text-stone-400 font-sans">
            总星宿: {{ checkpointResult.totalStars }}
          </div>
        </div>
      </div>

      <p class="text-xs text-muted mb-3">
        本关涉及的所有成语解析如下：
      </p>

      <!-- 本关全部成语卡片与释义 -->
      <div class="space-y-2.5">
        <div
          v-for="item in level.idioms"
          :key="item.id"
          class="rounded-xl border border-border bg-secondary/30 p-2.5 transition hover:border-primary/50"
        >
          <div class="flex items-baseline justify-between">
            <span class="font-heading text-base font-bold text-primary">{{ item.word }}</span>
            <span class="text-xs text-muted font-mono">
              {{ details[item.word]?.pinyin || '' }}
            </span>
          </div>
          <p class="mt-1 text-xs text-clay leading-relaxed">
            {{ details[item.word]?.explanation || '已解锁该词' }}
          </p>
          <div v-if="!isHero" class="mt-1.5 text-right">
            <a
              :href="'/game/chengyu-tianci-jielong/idiom/' + encodeURIComponent(item.word)"
              target="_blank"
              class="text-[11px] text-primary hover:underline font-medium inline-flex items-center gap-1"
            >
              <span>查看成语词条</span>
              <span>&rarr;</span>
            </a>
          </div>
        </div>
      </div>

      <!-- 操作按钮 -->
      <div class="mt-5 flex flex-col gap-2.5">
        <button
          type="button"
          @click="$emit('openLeaderboard')"
          class="w-full rounded-full border border-amber-800/40 bg-amber-50/80 py-2.5 text-sm font-bold text-amber-900 shadow-2xs hover:bg-amber-100 transition-all cursor-pointer flex items-center justify-center gap-1.5 font-serif"
        >
          <span>📜</span>
          <span>查看群英金榜</span>
        </button>

        <button
          v-if="!isHero"
          type="button"
          @click="$emit('share')"
          class="w-full rounded-full border border-primary bg-white py-2.5 text-sm font-bold text-primary shadow-sm transition-all cursor-pointer"
        >
          炫耀战绩
        </button>

        <button
          v-if="!isLast"
          type="button"
          @click="$emit('next')"
          class="w-full rounded-full bg-primary hover:bg-primary-hover py-2.5 text-sm font-bold text-white shadow-md transition-all cursor-pointer flex items-center justify-center gap-2"
        >
          <span>进入下一关</span>
          <span>&rarr;</span>
        </button>
        <div v-else class="text-center py-2">
          <p class="text-sm font-bold text-primary mb-3">
            🏆 您已成功通关全部关卡，太厉害了！
          </p>
          <button
            type="button"
            @click="$emit('restart')"
            class="w-full rounded-full border border-primary bg-white hover:bg-primary hover:text-white py-2.5 text-sm font-bold text-primary shadow-sm transition-all cursor-pointer"
          >
            重新挑战第 1 关
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { LevelItem, IdiomDetail } from '../../lib/types'
import type { CheckpointResultData } from '@/api/game'

withDefaults(
  defineProps<{
    show: boolean
    level: LevelItem
    isLast: boolean
    details: Record<string, IdiomDetail>
    isHero?: boolean
    checkpointResult?: CheckpointResultData | null
  }>(),
  {
    isHero: false,
    checkpointResult: null,
  }
)

defineEmits<{
  (e: 'close'): void
  (e: 'next'): void
  (e: 'share'): void
  (e: 'restart'): void
  (e: 'openLeaderboard'): void
}>()
</script>
